import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Subscription } from '../types/fish';
import type { SingleResponse } from '../types/api';

type SubscriptionResponse = any;
type DefaultError = unknown;

type UseSubscriptionParams = {
  subscriptionId?: string;
  include?: string;
  enabled?: boolean;
};

export function useSubscription<
  TData = SubscriptionResponse,
  TError = DefaultError,
>(
  { subscriptionId, include, enabled = true }: UseSubscriptionParams = {},
  options?: Omit<
    UseQueryOptions<SubscriptionResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['subscription', subscriptionId, include];

  return useQuery<SubscriptionResponse, TError, TData>({
    queryKey,
    enabled: enabled && !!subscriptionId,
    queryFn: async () => {
      if (!subscriptionId) {
        throw new Error('subscriptionId is required to fetch an subscription');
      }

      const response = (await service.getSubscriptionById(subscriptionId, {
        ...(include ? { include } : {}),
      })) as SingleResponse<Subscription>;

      return response;
    },
    ...(options as UseQueryOptions<
      SubscriptionResponse,
      TError,
      TData,
      QueryKey
    >),
  });
}
